import { ICurrency } from "src/app/features/customers/models/customers.models";
import { IProduct } from "src/app/features/produits/models/products.models";
import { TranslateService } from '@ngx-translate/core';

export interface ValidationResult {
    isValid: boolean;
    errors: string[];
}

export function validateSelectedProductsCurrencies(
    selectedProducts: IProduct[],
    availableCurrencies: ICurrency[],
    translate: TranslateService,
    source: 'agents' | 'catalogs' = 'catalogs',
    pricingRule?: 'discount' | 'margin' | 'discount_on_msrp' | 'margin_on_cost' | null,
    defaultCurrencyId?: number | null
  ): ValidationResult {
    const errors: string[] = [];
    
    // Check if we have available currencies
    if (!availableCurrencies || availableCurrencies.length === 0) {
      errors.push(translate.instant('Catalogs.validators.no_available_currencies'));
      return { isValid: false, errors };
    }
    
    // Check if we have selected products
    if (!selectedProducts || selectedProducts.length === 0) {
      errors.push(translate.instant('Catalogs.validators.no_products_selected'));
      return { isValid: false, errors };
    }
    
    const availableCurrencyIds = new Set(availableCurrencies.map((c: ICurrency) => c.id));
    
    // Check if pricing rule is margin
    const isMarginRule = pricingRule === 'margin' || pricingRule === 'margin_on_cost';
    
    // Get default currency for multi-currency validation
    const selectedCurrencyId = defaultCurrencyId || (availableCurrencies.length > 0 ? availableCurrencies[0].id : null);
    
    // Validate each selected product
    selectedProducts.forEach((product: IProduct, productIndex: number) => {
      const productName = product.name || product.sku || `Product ${productIndex + 1}`;
      
      // For margin rule, check if product has cost
      // Only add error if cost is missing AND final_price is also missing or <= 0
      // If final_price > 0, allow product even without cost
      if (isMarginRule) {
        // Check final_price first - if it's valid (> 0), don't block even if cost is missing
        let hasValidFinalPrice = false;
        
        if (source === 'catalogs' && product.currencies && product.currencies.length > 0 && selectedCurrencyId) {
          const selectedCurrency = product.currencies.find((c: any) => c.currency_id === selectedCurrencyId);
          hasValidFinalPrice = !!(selectedCurrency && selectedCurrency.final_price && selectedCurrency.final_price > 0);
        } else {
          hasValidFinalPrice = !!(product.final_price && product.final_price > 0);
        }
        
        // Only check cost if final_price is not valid
        if (!hasValidFinalPrice) {
          // For multi-currency, check cost in selected currency first
          if (source === 'catalogs' && product.currencies && product.currencies.length > 0 && selectedCurrencyId) {
            const selectedCurrency = product.currencies.find((c: any) => c.currency_id === selectedCurrencyId);
            if (!selectedCurrency || !selectedCurrency.cost || selectedCurrency.cost <= 0) {
              const currencyName = availableCurrencies.find(c => c.id === selectedCurrencyId)?.name || 'selected currency';
              errors.push(`${productName} (${currencyName}): ${translate.instant('Catalogs.validators.cost_required_currency')}`);
            }
          } else {
            // For single currency or agents, check cost at product level
            if (!product.cost || product.cost <= 0) {
              errors.push(`${productName}: ${translate.instant('Catalogs.validators.cost_required')}`);
            }
          }
        }
      }
      
      // For single currency mode (agents), check if product has required price
      if (source === 'agents') {
        const finalPrice = product.final_price;
        const salesPrice = product.sales_price;
        
        if (!finalPrice || finalPrice <= 0) {
          errors.push(`${productName}: ${translate.instant('Catalogs.validators.final_price_greater_than_zero')}`);
        } else if (!isMarginRule && salesPrice !== undefined && salesPrice !== null && salesPrice > 0 && finalPrice > salesPrice) {
          // Only check final_price > sales_price if NOT margin rule
          errors.push(`${productName}: ${translate.instant('Catalogs.validators.final_price_cannot_exceed_sales_price', { finalPrice, salesPrice })}`);
        }
        return; // Skip currency validation for agents
      }
      
      // For multi-currency mode (catalogs), validate currencies
      if (source === 'catalogs') {
        if (!product.currencies || product.currencies.length === 0) {
          errors.push(`${productName}: ${translate.instant('Catalogs.validators.no_currencies_configured')}`);
          return;
        }
        
        // Check if product has all available currencies
        const productCurrencyIds = new Set(product.currencies.map((c: any) => c.currency_id));
        const missingCurrencies = [...availableCurrencyIds].filter(id => !productCurrencyIds.has(id));
        
        if (missingCurrencies.length > 0) {
          const missingCurrencyNames = missingCurrencies
            .map(id => availableCurrencies.find((c: ICurrency) => c.id === id)?.name || `Currency ${id}`)
            .join(', ');
          errors.push(`${productName}: ${translate.instant('Catalogs.validators.missing_currencies', { missingCurrencies: missingCurrencyNames })}`);
        }
                
        // Check that every currency that exists in available currencies has a final price > 0 and <= sales price
        product.currencies.forEach((currency: any) => {
          // Skip currencies that are not in the available currencies list
          if (!availableCurrencyIds.has(currency.currency_id)) {
            return;
          }
          
          // In margin mode, only validate the selected currency
          // Other currencies can have final_price = 0 or null
          if (isMarginRule && selectedCurrencyId && currency.currency_id !== selectedCurrencyId) {
            return; // Skip validation for non-selected currencies in margin mode
          }
          
          const currencyName = currency.currency_name || `Currency ${currency.currency_id}`;
          const finalPrice = currency.final_price;
          const salesPrice = currency.sales_price;
          
          if (finalPrice === undefined || finalPrice === null) {
            errors.push(`${productName} (${currencyName}): ${translate.instant('Catalogs.validators.final_price_not_set')}`);
          } else if (finalPrice <= 0) {
            errors.push(`${productName} (${currencyName}): ${translate.instant('Catalogs.validators.final_price_must_be_greater_than_zero', { finalPrice })}`);
          } else if (!isMarginRule && salesPrice !== undefined && salesPrice !== null && salesPrice > 0 && finalPrice > salesPrice) {
            // Only check final_price > sales_price if NOT margin rule
            errors.push(`${productName} (${currencyName}): ${translate.instant('Catalogs.validators.final_price_cannot_exceed_sales_price_currency', { finalPrice, salesPrice })}`);
          }
        });
      }
    });
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validates selected products and returns a boolean result
   * @param selectedProducts Array of selected products
   * @param availableCurrencies Array of available currencies
   * @param translate Translation service
   * @param source Source context ('agents' or 'catalogs')
   * @returns true if all validations pass, false otherwise
   */
  export function areSelectedProductsValid(
    selectedProducts: IProduct[],
    availableCurrencies: ICurrency[],
    translate: TranslateService,
    source: 'agents' | 'catalogs' = 'catalogs',
    pricingRule?: 'discount' | 'margin' | 'discount_on_msrp' | 'margin_on_cost' | null,
    defaultCurrencyId?: number | null
  ): boolean {
    return validateSelectedProductsCurrencies(selectedProducts, availableCurrencies, translate, source, pricingRule, defaultCurrencyId).isValid;
  }

  /**
   * Gets validation errors for selected products
   * @param selectedProducts Array of selected products
   * @param availableCurrencies Array of available currencies
   * @param translate Translation service
   * @param source Source context ('agents' or 'catalogs')
   * @returns Array of error messages
   */
  export function getSelectedProductsValidationErrors(
    selectedProducts: IProduct[],
    availableCurrencies: ICurrency[],
    translate: TranslateService,
    source: 'agents' | 'catalogs' = 'catalogs',
    pricingRule?: 'discount' | 'margin' | 'discount_on_msrp' | 'margin_on_cost' | null,
    defaultCurrencyId?: number | null
  ): string[] {
    return validateSelectedProductsCurrencies(selectedProducts, availableCurrencies, translate, source, pricingRule, defaultCurrencyId).errors;
  }

  /**
   * Gets formatted tooltip message with first 3 errors and 'more' indicator
   * @param selectedProducts Array of selected products
   * @param availableCurrencies Array of available currencies
   * @param translate Translation service
   * @param source Source context ('agents' or 'catalogs')
   * @returns Formatted tooltip message
   */
  export function getValidationTooltipMessage(
    selectedProducts: IProduct[],
    availableCurrencies: ICurrency[],
    translate: TranslateService,
    source: 'agents' | 'catalogs' = 'catalogs',
    pricingRule?: 'discount' | 'margin' | 'discount_on_msrp' | 'margin_on_cost' | null,
    defaultCurrencyId?: number | null
  ): string {
    const errors = getSelectedProductsValidationErrors(selectedProducts, availableCurrencies, translate, source, pricingRule, defaultCurrencyId);
    if (errors.length === 0) {
      return '';
    }
    
    const firstThreeErrors = errors.slice(0, 3);
    let tooltipMessage = firstThreeErrors.join('\n\n');
    
    if (errors.length > 3) {
      const plural = errors.length - 3 > 1 ? 's' : '';
      tooltipMessage += `\n\n${translate.instant('Catalogs.validators.more_errors', { count: errors.length - 3, plural })}`;
    }
    
    return tooltipMessage;
  }

  /**
   * Gets global validation message
   * @param selectedProducts Array of selected products
   * @param availableCurrencies Array of available currencies
   * @param translate Translation service
   * @param source Source context ('agents' or 'catalogs')
   * @param pricingRule Pricing rule ('discount' | 'margin' | 'discount_on_msrp' | 'margin_on_cost')
   * @param defaultCurrencyId Default currency ID
   * @returns Global validation message
   */
  export function getGlobalValidationMessage(
    selectedProducts: IProduct[],
    availableCurrencies: ICurrency[],
    translate: TranslateService,
    source: 'agents' | 'catalogs' = 'catalogs',
    pricingRule?: 'discount' | 'margin' | 'discount_on_msrp' | 'margin_on_cost' | null,
    defaultCurrencyId?: number | null
  ): string {
    const errors = getSelectedProductsValidationErrors(selectedProducts, availableCurrencies, translate, source, pricingRule, defaultCurrencyId);
    if (errors.length === 0) {
      return '';
    }
    
    // Check if we're in margin_on_cost mode and if there are cost-related errors
    const isMarginRule = pricingRule === 'margin' || pricingRule === 'margin_on_cost';
    const costErrors = errors.filter(error => 
      error.includes(translate.instant('Catalogs.validators.cost_required')) || 
      error.includes(translate.instant('Catalogs.validators.cost_required_currency'))
    );
    
    // If in margin mode and there are cost errors, show specific message
    if (isMarginRule && costErrors.length > 0) {
      if (costErrors.length === 1) {
        return translate.instant('Catalogs.validators.cost_missing_single');
      }
      return translate.instant('Catalogs.validators.cost_missing_multiple', { count: costErrors.length });
    }
    
    if (errors.length === 1) {
      return translate.instant('Catalogs.validators.validation_error_single');
    }
    
    return translate.instant('Catalogs.validators.validation_errors_multiple', { count: errors.length });
  }
